import { useState, useEffect, useMemo } from "react";
import {
  API_BASE_URL,
  API_PUBLIC_AUTH_TOKEN,
  API_ENDPOINTS,
} from "../constants/api";
import { RCODES } from "../constants/rcodes";
import {
  MaintenanceConfigResponse,
  MaintenanceState,
} from "../types/maintenance-page";
import state from "../maintenance-page/state.json";

// Cache the maintenance state to avoid repeated fetches
let cachedMaintenanceState: MaintenanceState | null = null;

// Export the cached maintenance state for direct access in other modules
export const getCachedMaintenanceState = () => cachedMaintenanceState;

export const useMaintenance = () => {
  const [maintenanceState, setMaintenanceState] = useState<MaintenanceState>(
    cachedMaintenanceState || (state as MaintenanceState)
  );
  const [isLoading, setIsLoading] = useState(!cachedMaintenanceState);

  useEffect(() => {
    // If we already have cached state, don't fetch again
    if (cachedMaintenanceState) {
      setIsLoading(false);
      return;
    }

    const fetchMaintenanceConfig = async () => {
      try {
        const url = `${API_BASE_URL}${API_ENDPOINTS.MAINTENANCE_CONFIG}`;
        const headers: Record<string, string> = {
          "Content-Type": "application/json",
        };

        if (API_PUBLIC_AUTH_TOKEN) {
          headers.Authorization = API_PUBLIC_AUTH_TOKEN;
        }

        const response = await fetch(url, {
          method: "GET",
          headers,
        });

        const data: MaintenanceConfigResponse = await response.json();

        if (data.rcode === RCODES.SUCCESS) {
          const newState: MaintenanceState = {
            enabled: data.enabled,
            dateTime: data.dateTime,
          };
          cachedMaintenanceState = newState;
          setMaintenanceState(newState);
        } else {
          // On error, fall back to local state.json
          console.error(
            "Failed to fetch maintenance config, using local fallback:",
            data.rmsg
          );
        }
      } catch (error) {
        console.error("Error fetching maintenance config:", error);
        // On error, fall back to local state.json (already set as default)
      } finally {
        setIsLoading(false);
      }
    };

    fetchMaintenanceConfig();
  }, []);

  return useMemo(
    () => ({
      maintenanceState,
      isLoading,
    }),
    [maintenanceState, isLoading]
  );
};
